import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "../App";
import { SEO } from "../components/SEO";
import axios from "axios";
import { Button } from "../components/ui/button";
import { MapPin, CalendarDays, Loader2, Route, ArrowRight, User } from "lucide-react";
import { format, parseISO, isValid } from "date-fns";

const fmtDate = (s, f) => {
  if (!s) return "";
  try { const d = parseISO(s); return isValid(d) ? format(d, f) : s; }
  catch { return s; }
};

const SharedTrip = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/trips/public/${shareId}`);
        setTrip(data);
      } catch {
        setError("This trip link is invalid or has been removed.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [shareId]);

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  if (error || !trip) {
    return (
      <div className="page-container flex flex-col items-center justify-center px-6 text-center">
        <div className="w-16 h-16 rounded-2xl bg-red-500/15 flex items-center justify-center mb-4">
          <MapPin className="w-8 h-8 text-red-400" />
        </div>
        <h1 className="text-xl font-bold text-white mb-2">Trip not found</h1>
        <p className="text-slate-400 text-sm mb-6">{error}</p>
        <Button onClick={() => navigate("/")} className="bg-cyan-500 hover:bg-cyan-400 text-white rounded-full px-6">
          Go to Explorio
        </Button>
      </div>
    );
  }

  const dateRange = `${fmtDate(trip.start_date, "MMM d")} - ${fmtDate(trip.end_date, "MMM d, yyyy")}`;
  const sights = trip.roadtrip_data?.sights || [];
  const destinations = trip.roadtrip_data?.destinations || [];

  return (
    <div className="page-container">
      <SEO
        title={`${trip.name} - ${trip.destination_country}`}
        description={`${trip.owner_name}'s trip to ${trip.destination_country}. ${dateRange}.`}
        path={`/trip/${shareId}`}
      />

      {/* Hero */}
      <div className="relative h-56 sm:h-72 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200&h=630&fit=crop&q=80"
          alt={trip.destination_country}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1a] via-[#0a0f1a]/50 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <nav className="flex items-center gap-2 mb-3">
            <img src="/logo192.png" alt="Explorio" className="w-7 h-7 rounded-lg" />
            <span className="text-sm font-semibold text-white/80">Explorio</span>
          </nav>
          <h1 className="text-3xl sm:text-4xl font-bold text-white" data-testid="shared-trip-title">{trip.name}</h1>
        </div>
      </div>

      {/* Trip Details */}
      <div className="max-w-2xl mx-auto px-5 py-6 space-y-5">
        {/* Meta row */}
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2 text-slate-300">
            <MapPin className="w-4 h-4 text-cyan-400" />
            <span data-testid="shared-trip-destination">{trip.destination_country}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-300">
            <CalendarDays className="w-4 h-4 text-violet-400" />
            <span data-testid="shared-trip-dates">{dateRange}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-300">
            <User className="w-4 h-4 text-amber-400" />
            <span data-testid="shared-trip-owner">By {trip.owner_name}</span>
          </div>
        </div>

        {/* Notes */}
        {trip.notes && (
          <div className="p-4 rounded-xl border border-white/8 bg-white/[0.04]">
            <p className="text-sm text-slate-300 leading-relaxed" data-testid="shared-trip-notes">{trip.notes}</p>
          </div>
        )}

        {/* Route */}
        {destinations.length > 0 && (
          <div className="p-4 rounded-xl border border-sky-500/20 bg-sky-500/5">
            <div className="flex items-center gap-2 mb-2">
              <Route className="w-4 h-4 text-sky-400" />
              <span className="text-sm font-medium text-sky-300">Route</span>
            </div>
            <p className="text-sm text-slate-300" data-testid="shared-trip-route">
              {destinations.map(d => d.city).join(" → ")}
            </p>
          </div>
        )}

        {/* Sights */}
        {sights.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-white mb-3">Planned Sights ({sights.length})</h2>
            <div className="space-y-2">
              {sights.map((sight, i) => (
                <div key={i} className="p-3 rounded-xl border border-white/8 bg-white/[0.03]">
                  <p className="text-sm font-medium text-white">{sight.name}</p>
                  {sight.location && <p className="text-xs text-slate-500 mt-0.5">{sight.location}</p>}
                  {sight.description && <p className="text-xs text-slate-400 mt-1 line-clamp-2">{sight.description}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="pt-4 border-t border-white/8">
          <div className="text-center">
            <p className="text-slate-400 text-sm mb-3">Want to plan your own trip?</p>
            <Button
              onClick={() => navigate("/")}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white rounded-full px-8 h-11 font-medium"
              data-testid="shared-trip-cta"
            >
              Start Planning with Explorio
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharedTrip;
